import styled, { css } from 'styled-components';
import React, { useMemo } from 'react';
import responsive from '@/se/utilities/responsive';
import { PhysicianCategory, PhysicianSpecialityColors } from '../../entities/physician/enums';
import { fullNameToDrLastName } from '../kiosk/schedule/shared/procedureUtils';
import Tooltip from '../../Tooltip';
import { roundValue } from '../Analytics';
import { WarningRounded } from '@material-ui/icons';

const utilizedColor = '#00A7F7';
const actualColor = '#99E6B3';

export const CellBasis = styled.div`
  padding: 0.583em 0.5em 0.583em 0.75em;
  height: 4em;
  display: flex;
  align-items: center;
  position: relative;
  ${props =>
    props.grayed &&
    css`
      filter: brightness(80%);
    `}
`;

export const HeaderCell = styled(CellBasis)`
  background-color: rgba(255, 255, 255, 0.08);
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.35);
  flex-flow: column;

  padding: 0.583em 0.5em;
  height: 100%;
`;

const CellContainer = styled(CellBasis)`
  background-color: ${props => props.color || '#313a64'};
  position: absolute;
  left: calc(${props => props.start}%);
  right: calc(100% - ${props => props.end}%);
  cursor: pointer;
  :hover {
    opacity: 0.9;
  }
  :active {
    opacity: 0.8;
  }

  ${props =>
    props.highlighted &&
    css`
      background-color: #5a67a1;
    `}
  transition: all .2s ease;
  > span {
    display: flex;
    width: 100%;
    height: 100%;
    overflow: hidden;
    align-items: center;
  }
`;

export const EmptyCell = styled(CellContainer)`
  background-color: transparent;
  :hover {
    background-color: #273a60 !important;
  }
  :active {
    background-color: #313a64 !important;
  }

  transition: all 0.2s ease;
`;

const BookedBy = styled.div`
  display: flex;
  align-items: center;
  line-height: 1.4;
  font-weight: 500;
  overflow: hidden;
  white-space: nowrap;
  z-index: 1;
  ${responsive.md.andSmaller`
    word-break: break-all;
    flex-direction: column;
    line-height: 1.4;

    > span {
      margin-top: .25em;
    }
  `}
`;

const SpecialtyIndicator = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: 0.25em;
  background-color: ${props => (props.color ? props.color : 'rgba(255,255,255,.1)')};
`;

const Utilization = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: ${props => props.percent}%;
  background-color: ${utilizedColor};
  opacity: 0.65;
  z-index: 0;
`;

const UtilizationActual = styled.div`
  position: absolute;
  top: 25%;
  bottom: 25%;
  left: 0;
  width: ${props => props.percent}%;
  background: ${actualColor};
  opacity: 0.65;
  z-index: 1;
`;

const Label = styled.span`
  color: #fff;
  font-size: 0.75rem;
  font-weight: 500;
  text-shadow:
    -1px -1px 0 #000c3f,
    1px -1px 0 #000c3f,
    -1px 1px 0 #000c3f,
    1px 1px 0 #000c3f;
`;

const StyledWarningIcon = styled(WarningRounded)`
  color: #ffd700;
  font-size: 1.2em !important;
  filter: drop-shadow(-1px -1px 0 #000c3f) drop-shadow(1px -1px 0 #000c3f) drop-shadow(-1px 1px 0 #000c3f)
    drop-shadow(1px 1px 0 #000c3f);
`;

const StyledErrorIcon = styled(WarningRounded)`
  color: #ff4444;
  font-size: 1.2em !important;
  filter: drop-shadow(-1px -1px 0 #000c3f) drop-shadow(1px -1px 0 #000c3f) drop-shadow(-1px 1px 0 #000c3f)
    drop-shadow(1px 1px 0 #000c3f);
`;

const IconArea = styled.span`
  position: absolute;
  right: 0.5em;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
`;

const formatDurationFromMinutes = minutes => {
  const total = Math.max(0, Math.round(Number(minutes) || 0));
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  const parts = [];
  if (hours > 0) parts.push(`${hours} ${hours === 1 ? 'hr' : 'hrs'}`);
  if (mins > 0 || hours === 0) parts.push(`${mins} min`);
  return parts.join(' ');
};

const colorForCategory = category => {
  switch (category) {
    case PhysicianCategory.Endoscopy: {
      return '#FFAC00';
    }
    case PhysicianCategory.Eye: {
      return '#F55323';
    }
    case PhysicianCategory.Pain: {
      return '#50E3C2';
    }
    case PhysicianCategory.Surgical: {
      return '#7ED321';
    }
    default: {
      return '#313a64';
    }
  }
};

const colorForSpeciality = speciality => {
  if (speciality in PhysicianSpecialityColors) {
    return PhysicianSpecialityColors[speciality].color;
  }
  return PhysicianSpecialityColors.default.color;
};

const Cell = ({
  physician,
  organization,
  category,
  speciality,
  onClick,
  grayed,
  highlighted,
  start,
  end,
  colorMode,
  utilization,
  utilizationOutsideSlot,
  actualUtilization,
  actualUtilizationOutsideSlot,
  utilizationOutsideSlotDurationHours,
  actualUtilizationOutsideSlotDurationHours,
}) => {
  const tooltipContent = useMemo(
    () =>
      `<span style="display: flex; text-align: left">
        ${organization || (physician ? `Dr. ${physician}` : 'Open Block')}
        <br/>Speciality: ${speciality ?? ''}
        <br/>Scheduled %: ${roundValue(utilization)} %
        <br/>Actual %: ${roundValue(actualUtilization)} %
      </span>`,
    [actualUtilization, organization, physician, speciality, utilization]
  );

  const specialityColor = useMemo(() => colorForSpeciality(speciality), [speciality]);
  const roundedUtilization = useMemo(() => roundValue(utilization), [utilization]);
  const roundedActualUtilization = useMemo(() => roundValue(actualUtilization), [actualUtilization]);
  const scheduledOutsideMinutes = useMemo(() => {
    const hours = Number(utilizationOutsideSlotDurationHours ?? 0);
    return Math.round(hours * 60);
  }, [utilizationOutsideSlotDurationHours]);
  const actualOutsideMinutes = useMemo(() => {
    const hours = Number(actualUtilizationOutsideSlotDurationHours ?? 0);
    return Math.round(hours * 60);
  }, [actualUtilizationOutsideSlotDurationHours]);
  const scheduledIndicatorTooltip = useMemo(() => {
    if (!utilizationOutsideSlot) return '';
    const formatted = formatDurationFromMinutes(scheduledOutsideMinutes);
    return `<span>Scheduled +${formatted} past block</span>`;
  }, [scheduledOutsideMinutes, utilizationOutsideSlot]);
  const actualIndicatorTooltip = useMemo(() => {
    if (!actualUtilizationOutsideSlot) return '';
    const actualFormatted = formatDurationFromMinutes(actualOutsideMinutes);
    if (utilizationOutsideSlot) {
      const scheduledFormatted = formatDurationFromMinutes(scheduledOutsideMinutes);
      return `<span>Actual +${actualFormatted} past block<br/>Scheduled +${scheduledFormatted} past block</span>`;
    }
    return `<span>Actual +${actualFormatted} past block</span>`;
  }, [actualOutsideMinutes, actualUtilizationOutsideSlot, scheduledOutsideMinutes, utilizationOutsideSlot]);
  const label = useMemo(
    () => organization || (physician ? (end - start < 40 ? fullNameToDrLastName(physician) : physician) : 'Open Block'),
    [end, organization, physician, start]
  );

  return (
    <CellContainer
      onClick={onClick}
      grayed={grayed}
      highlighted={highlighted}
      start={start}
      end={end}
      color={colorMode ? colorForCategory(category) : undefined}
    >
      <Tooltip delay={300} content={tooltipContent}>
        <Utilization percent={roundedUtilization} />
        <UtilizationActual percent={roundedActualUtilization} />
        {!colorMode && (
          <BookedBy>
            <SpecialtyIndicator color={specialityColor} />
            <Label>{label}</Label>
          </BookedBy>
        )}
      </Tooltip>
      {actualUtilizationOutsideSlot ? (
        <Tooltip delay={200} content={actualIndicatorTooltip}>
          <IconArea>
            <StyledErrorIcon />
          </IconArea>
        </Tooltip>
      ) : (
        utilizationOutsideSlot && (
          <Tooltip delay={200} content={scheduledIndicatorTooltip}>
            <IconArea>
              <StyledWarningIcon />
            </IconArea>
          </Tooltip>
        )
      )}
    </CellContainer>
  );
};

export default Cell;
